import React from "react";
import SEO from '../components/SEO';
import AdBanner from '../components/AdBanner';
import AdComponent from '../components/AdComponent';
import HilltopBanner from '../components/HilltopBanner';

export default function AboutUs() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="CalcBlog - About Us"
        description="Your destination for helpful calculators and insightful articles. Making complex calculations simple and accessible."
        keywords="calcblog, online calculators, health calculators, bmi calculator, calorie calculator, sleep optimization calculator, carbon footprint calculator, monthly budget calculator, personal finance calculator, sustainability calculators, exosome skincare calculator, ai powered learning tools, interactive calculators, free online calculators, educational calculators, calculator blog, best calculators online, calculator for students, calculator for health, calculator for finance, calculator for environment"
        toolType="blog"
      />

      {/* 3-COLUMN WRAPPER */}
      <div className="flex flex-col lg:flex-row max-w-[1440px] mx-auto gap-4 px-4 py-12">
        
        {/* LEFT COLUMN (ADS) - Sticky on Desktop */}
        <aside className="hidden lg:flex w-[160px] shrink-0 justify-center">
          <div className="sticky top-24 h-fit">
            <AdBanner adKey="f944541495caabb1dbef72e5e10873f3" width={160} height={600} instanceId="ad7" />
          </div>
        </aside>

        {/* CENTER COLUMN (MAIN CONTENT) */}
        <main aria-labelledby="aboutus-title" className="flex-1 min-w-0 px-0 sm:px-6 lg:px-8">
          
          {/* Hero */}
          <section className="text-left">
            <h1
              id="aboutus-title"
              className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white"
            >
              About CalcBlog
            </h1>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
              We turn complex topics into clear, interactive calculators and
              concise guides—so anyone can make smart, confident decisions.
            </p>
          </section>

          {/* Divider */}
          <hr className="my-10 border-gray-200 dark:border-gray-700" />

          {/* Mission + Vision */}
          <section
            aria-labelledby="mission-vision-title"
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            <div>
              <h2 id="mission-vision-title" className="text-2xl font-semibold text-gray-900 dark:text-white">
                Our mission
              </h2>
              <p className="mt-3 text-gray-700 dark:text-gray-300">
                Make education practical. CalcBlog blends playful design with robust
                data and engineering to deliver tools that help you measure, plan,
                and improve without the jargon.
              </p>
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Our vision
              </h2>
              <p className="mt-3 text-gray-700 dark:text-gray-300">
                A world where anyone can explore health, sustainability, and finance
                through interactive calculators that are accurate, accessible, and
                delightful to use.
              </p>
            </div>
          </section>

          {/* Focus Areas */}
          <section aria-labelledby="focus-areas-title" className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            <h2 id="focus-areas-title" className="sr-only">Focus areas</h2>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-900">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Health & Nutrition</h3>
              <p className="mt-2 text-gray-700 dark:text-gray-300">
                Calorie and BMI calculators, sleep optimization tools, and clear guides.
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-900">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Sustainability</h3>
              <p className="mt-2 text-gray-700 dark:text-gray-300">
                Carbon footprint explainers and interactive tools to help act on climate goals.
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-900">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Personal Finance</h3>
              <p className="mt-2 text-gray-700 dark:text-gray-300">
                Monthly budgeting and simple frameworks that put clarity back in your hands.
              </p>
            </div>
          </section>
          <div className="h-64 mt-8 flex justify-center">
            { /*<AdComponent />*/ }
            <HilltopBanner/> 
          </div>  
          {/* Values */}
          <section aria-labelledby="values-title" className="mt-12">
            <h2 id="values-title" className="text-2xl font-semibold text-gray-900 dark:text-white">
              What we value
            </h2>
            <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
              <li className="rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-5">
                <p className="font-medium text-gray-900 dark:text-white">Clarity</p>
                <p className="mt-1 text-gray-700 dark:text-gray-300">Plain‑language explanations and clean UI.</p>
              </li>
              <li className="rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-5">
                <p className="font-medium text-gray-900 dark:text-white">Accuracy</p>
                <p className="mt-1 text-gray-700 dark:text-gray-300">Evidence‑based content you can trust.</p>
              </li>
            </ul>

          </section>

          {/* Story */}
          <section aria-labelledby="story-title" className="mt-12">
            <h2 id="story-title" className="text-2xl font-semibold text-gray-900 dark:text-white">Our story</h2>
            <p className="mt-3 text-gray-700 dark:text-gray-300">
              CalcBlog began as a passion project: build tools that bridge playful
              design with real‑world utility.
            </p>
          </section>

          {/* Footer note */}
          <section aria-labelledby="footer-note-title" className="mt-12">
            <h2 id="footer-note-title" className="sr-only">Footer note</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              © {new Date().getFullYear()} CalcBlog. Tools for clear thinking.
            </p>
          </section>
        </main>

        {/* RIGHT COLUMN (ADS) - Sticky on Desktop */}
        <aside className="w-full lg:w-[160px] shrink-0 flex justify-center">
          <div className="lg:sticky lg:top-24 h-fit">
          </div>
        </aside>

      </div>
    </div>
  );
}